import classNames from "classnames";
import { History } from "history";
import { groupBy, keyBy } from "lodash";
import { computed } from "mobx";
import { inject, observer } from "mobx-react";
import prettyBytes from "pretty-bytes";
import React from "react";
import "react-image-lightbox/style.css";
import { FormattedMessage } from "react-intl";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import Tooltip from "react-tooltip-lite";
import { compose } from "recompose";

import PageTitle from "nvent-web/App/components/PageTitle";
import { SecondaryRedButton } from "nvent-web/components/Button";
import FileButton, { defaultMaxSize, ValidatedFile } from "nvent-web/components/Button/FileButton";
import LinkButton from "nvent-web/components/Button/LinkButton";
import FormattedMeasurement from "nvent-web/components/FormattedMeasurement/FormattedMeasurement";
import { HelpTip } from "nvent-web/components/HelpTip";
import ItemList from "nvent-web/components/ItemList";
import { LoadingSection } from "nvent-web/components/LoadingSection";
import Modal from "nvent-web/components/Modal";
import ConfirmationModal from "nvent-web/components/Modal/ConfirmationModal";
import ProgressFull from "nvent-web/components/Progress/FullProgress";
import TranslatedString from "nvent-web/components/TranslatedString/TranslatedString";
import { COMPLETED_PROGRESS } from "nvent-web/scenes/ProjectDetails";
import { PhotosStore } from "nvent-web/stores/Photos";
import { ProductsStore } from "nvent-web/stores/Products";
import { ProjectsStore } from "nvent-web/stores/Projects";
import { RoomsStore } from "nvent-web/stores/Rooms";
import { DetailedProject } from "nvent-web/types/DetailedProject";
import { DetailedRoom } from "nvent-web/types/DetailedRoom";
import { Product } from "nvent-web/types/Product";

import { NotificationsStore } from "nvent-web/stores/Notifications";
import AccessoryItem from "./components/AccessoryItem";
import AddAccessoryForm from "./components/AddAccessoryForm";
import AddProductForm from "./components/AddProductForm";
import AddThermostatForm from "./components/AddThermostatForm";
import { CableSpacingWarningModal } from "./components/CableSpacingWarningModal/CableSpacingWarningModal";
import PhotoGrid from "./components/PhotoGrid/PhotoGrid";
import ProductItem from "./components/ProductItem";
import ThermostatItem from "./components/ThermostatItem";
import style from "./RoomDetails.module.scss";
import { BundledAccessory, BundledThermostat } from "./types";

interface RoomDetailsParams {
  projectId: string;
  roomId: string;
}

interface ProjectsProps extends RouteComponentProps<RoomDetailsParams> {
  rooms: RoomsStore;
  projects: ProjectsStore;
  photos: PhotosStore;
  products: ProductsStore;
  notifications: NotificationsStore;
  history: History;
}

interface RoomDetailsState {
  isAddProductOpen: boolean;
  isAddThermostatOpen: boolean;
  isAddAccessoryOpen: boolean;
  isCableSpacingWarningModalOpen: boolean;
  isRemoveAllProductsOpen: boolean;
}

const MIN_CABLE_SPACING = 0.05;
const MAX_CABLE_SPACING = 0.25;
const MIN_POWER_PER_M2 = 50;

export class RoomDetails extends React.Component<ProjectsProps, RoomDetailsState> {
  state = {
    isAddProductOpen: false,
    isAddThermostatOpen: false,
    isAddAccessoryOpen: false,
    isCableSpacingWarningModalOpen: false,
    isRemoveAllProductsOpen: false,
  };

  @computed
  get isLoading() {
    return (
      this.props.rooms.areRoomDetailsLoading ||
      this.props.projects.isProjectLoading ||
      this.props.products.isProductCatalogLoading
    );
  }

  @computed
  get projectDetails() {
    return this.props.projects.projectDetails;
  }

  @computed
  get roomDetails() {
    return this.props.rooms.roomDetails;
  }

  @computed
  get products() {
    return this.roomDetails ? this.roomDetails.products : [];
  }

  @computed
  get thermostats(): BundledThermostat[] {
    const skus = this.props.products.productSkus;
    const skusById = keyBy(skus, "id");

    const explicit = this.roomDetails ? this.roomDetails.thermostats : [];
    const bundled = this.products
      .map(
        (product, index): BundledThermostat | null => {
          const { thermostatId: id } = product;

          if (id === null || id === undefined || !skusById[id]) {
            return null;
          }

          const { identifier, description } = skusById[id];

          // HACK: shove an SKU into the Thermostat interface...
          return {
            identifier,
            description,
            id: -(index + 1), // HACK: avoid potential conflicts with explicit ids
            productCategoryId: 0,
            source: product,
          };
        }
      )
      .filter((thermostat): thermostat is BundledThermostat => thermostat !== null);

    return [...bundled, ...explicit];
  }

  @computed
  get accessories(): BundledAccessory[] {
    return this.roomDetails ? this.roomDetails.accessories : [];
  }

  @computed
  get hasAnyProduct() {
    return this.products.length > 0 || this.thermostats.length > 0 || this.accessories.length > 0;
  }

  private get params() {
    const rawParams = this.props.match.params;

    return {
      ...rawParams,
      projectId: Number(rawParams.projectId),
      roomId: Number(rawParams.roomId),
    };
  }

  componentDidMount() {
    const { projects, rooms } = this.props;
    const { projectId, roomId } = this.params;

    projects.getProject(projectId);
    rooms.getRoom(projectId, roomId);
  }

  renderEditButton(projectDetails: DetailedProject, roomId: number) {
    return (
      <>
        {!projectDetails.finished && (
          <Link to={`/projects/${projectDetails.id}/rooms/${roomId}/edit`}>
            <FormattedMessage id="projects.details.edit" />
          </Link>
        )}
      </>
    );
  }

  renderInstallableArea(installableArea: number, area: number) {
    return (
      <span className={style.area}>
        {installableArea ? `${installableArea} m²` : "-"} / {area ? `${area} m²` : "-"}
      </span>
    );
  }

  renderCableSpacing = (cableSpacing: number) => {
    const isCableSpacingInvalid = cableSpacing < MIN_CABLE_SPACING || cableSpacing > MAX_CABLE_SPACING;
    const cableSpacingElement = <FormattedMeasurement value={cableSpacing * 1000} fractionDigits={0} unit="mm" />;

    return isCableSpacingInvalid ? (
      <Tooltip
        direction="left"
        content={<FormattedMessage id="roomCard.details.invalidCableSpacing" />}
        className={style.tooltip}
        tipContentClassName={style.tipContent}
      >
        <span className={style.invalidValue}>{cableSpacingElement}</span>
        <span className={style.tooltipQuestionMark}>?</span>
      </Tooltip>
    ) : (
      cableSpacingElement
    );
  };

  renderPowerPerM2 = (powerPerM2: number) => {
    const isPowerTooLow = powerPerM2 < MIN_POWER_PER_M2;
    const powerPerM2Element = <FormattedMeasurement value={powerPerM2} fractionDigits={0} unit="W" />;

    return isPowerTooLow ? (
      <Tooltip
        direction="left"
        content={<FormattedMessage id="roomCard.details.invalidPowerPerM2" />}
        className={style.tooltip}
        tipContentClassName={style.tipContent}
      >
        <span className={style.invalidValue}>{powerPerM2Element}</span>
        <span className={style.tooltipQuestionMark}>?</span>
      </Tooltip>
    ) : (
      powerPerM2Element
    );
  };

  renderDetails(roomDetails: DetailedRoom, projectDetails: DetailedProject) {
    const {
      id,
      name,
      progress,
      installableArea,
      area,
      floorFinish,
      cableSpacing,
      powerPerM2,
      totalOutput,
    } = roomDetails;
    const { floorFinishOptions } = this.props.rooms;

    const floorFinishOption = floorFinishOptions.find((option) => option.id === floorFinish);

    const output = totalOutput;
    return (
      <>
        <div className={style.row}>
          <Link to={`/projects/${projectDetails.id}`}>{projectDetails.name}</Link>
          <span className={style.editButtonMobileWrapper}>{this.renderEditButton(projectDetails, id)}</span>
        </div>
        <div className={style.row}>
          <p className={style.name}>{name}</p>
          <span className={style.areaMobileWrapper}>{this.renderInstallableArea(installableArea, area)}</span>
          <span className={style.editButtonDesktopWrapper}>{this.renderEditButton(projectDetails, id)}</span>
        </div>
        <span className={style.areaDesktopWrapper}>{this.renderInstallableArea(installableArea, area)}</span>
        <div className={classNames(style.row, style.floorFinishRow)}>
          <div className={style.detailsWrapper}>
            <p className={style.detailsLabel}>
              <FormattedMessage id="rooms.details.floorFinish" />
            </p>
            <span className={style.material}>
              {floorFinishOption ? <TranslatedString value={floorFinishOption.name} /> : "-"}
            </span>
          </div>

          <div className={style.detailsWrapper}>
            <p className={style.detailsLabel}>
              <FormattedMessage id="rooms.details.outputPerSquareMeter" />
            </p>
            <span className={style.material}>
              {powerPerM2 ? this.renderPowerPerM2(powerPerM2) : this.renderToolTip("up-end", "outputPerSquareMeter")}
            </span>
          </div>

          <div className={style.detailsWrapper}>
            <p className={style.detailsLabel}>
              <FormattedMessage id="rooms.details.output" />
            </p>
            <span className={style.material}>
              {output ? <FormattedMeasurement value={output} fractionDigits={2} unit="W" /> : "-"}
            </span>
          </div>

          <div className={style.detailsWrapper}>
            <p className={style.detailsLabel}>
              <FormattedMessage id="rooms.details.cableSpacing" />
            </p>
            <span className={style.material}>
              {cableSpacing ? this.renderCableSpacing(cableSpacing) : this.renderToolTip("down-end", "cableSpacing")}
            </span>
          </div>
        </div>

        {!projectDetails.finished && (
          <div className={classNames(style.row, style.progressRow)}>
            <ProgressFull progress={progress} />
          </div>
        )}
      </>
    );
  }

  render() {
    const { arePhotosCreating } = this.props.photos;
    const {
      isAddProductOpen,
      isAddThermostatOpen,
      isRemoveAllProductsOpen,
      isAddAccessoryOpen,
      isCableSpacingWarningModalOpen,
    } = this.state;

    const { isLoading, projectDetails, roomDetails, hasAnyProduct, products, thermostats, accessories } = this;

    if (isLoading && !roomDetails) {
      return <LoadingSection />;
    }

    return (
      <>
        <PageTitle>
          <FormattedMessage id="rooms.details.title" />
        </PageTitle>
        {roomDetails && projectDetails && (
          <>
            <div className={style.wrapper}>
              {projectDetails && <div className={style.inner}>{this.renderDetails(roomDetails, projectDetails)}</div>}
            </div>
            <div className={style.actions}>
              {projectDetails.progress === COMPLETED_PROGRESS && (
                <LinkButton className={style.linkButton} theme="primaryBlue" to={`/projects/${projectDetails.id}/`}>
                  <FormattedMessage id="actions.next" />
                </LinkButton>
              )}
            </div>
            {hasAnyProduct && (
              <SecondaryRedButton className={style.deleteAllButton} onClick={this.openRemoveAllProductsModal}>
                <FormattedMessage id="actions.removeAll" />
              </SecondaryRedButton>
            )}
            <ItemList
              title={<FormattedMessage id="rooms.details.productsList" />}
              onAdd={this.addProduct}
              disableAdd={projectDetails.finished}
              required
            >
              {products.length > 0 && this.productsList(products, projectDetails.finished)}
            </ItemList>
            <ItemList
              title={<FormattedMessage id="rooms.details.thermostatList" />}
              onAdd={this.addThermostat}
              disableAdd={projectDetails.finished}
            >
              {thermostats.length > 0 && this.thermostatsList(thermostats, projectDetails.finished)}
            </ItemList>
            <ItemList
              title={<FormattedMessage id="rooms.details.accessoriesList" />}
              onAdd={this.addAccessory}
              disableAdd={projectDetails.finished}
            >
              {accessories.length > 0 && this.accessoriesList(accessories, projectDetails.finished)}
            </ItemList>
            <div className={style.productListHeader}>
              <div>
                <FormattedMessage id="rooms.details.photos" />
                <span>*</span>
                {this.renderToolTip("up", "photos")}
              </div>
              {!projectDetails.finished && (
                <FileButton
                  multiple={true}
                  disabled={arePhotosCreating}
                  loading={arePhotosCreating}
                  theme="secondaryBlue"
                  onChange={this.handleAddPhotos}
                  className={style.fileButton}
                >
                  <FormattedMessage id="actions.add" />
                </FileButton>
              )}
            </div>
            <div className={style.listWrapper}>
              <PhotoGrid removeImage={this.handleRemoveImage} photos={roomDetails.photos || []} />
            </div>
            <Modal center isOpen={isAddProductOpen} handleClose={this.handleAddProductClose}>
              <AddProductForm onClose={this.handleAddProductClose} onSuccess={this.checkIfSufficientCableSpacing} />
            </Modal>
            <Modal center isOpen={isAddThermostatOpen} handleClose={this.handleAddThermostatClose}>
              <AddThermostatForm onClose={this.handleAddThermostatClose} />
            </Modal>
            <Modal center isOpen={isAddAccessoryOpen} handleClose={this.handleAddThermostatClose}>
              <AddAccessoryForm onClose={this.handleAddAccessoryClose} />
            </Modal>
            <ConfirmationModal
              isOpen={isRemoveAllProductsOpen}
              handleClose={this.closeRemoveAllProductsModal}
              center
              title={<FormattedMessage id={"products.removeAllModal.title"} />}
              description={<FormattedMessage id={"products.removeAllModal.description"} />}
              onCancel={this.closeRemoveAllProductsModal}
              onConfirm={this.deleteAllProducts}
            />
            <Modal center isOpen={isCableSpacingWarningModalOpen} handleClose={this.closeCableSpacingWarningModal}>
              <CableSpacingWarningModal onClose={this.closeCableSpacingWarningModal} />
            </Modal>
          </>
        )}
      </>
    );
  }

  renderTabs() {
    return (
      <>
        <div className={style.tabs} />
      </>
    );
  }

  private handleRemoveImage = (imageId: number) => {
    const { match } = this.props;
    const projectId = Number(match.params.projectId);
    const roomId = Number(match.params.roomId);

    this.props.photos.deletePhoto(projectId, roomId, imageId);
  };

  private handleAddPhotos = async (photos: ValidatedFile[]) => {
    const validPhotos = photos.filter(({ isValid }) => isValid);
    const invalidPhotos = photos.filter(({ isValid }) => !isValid);

    invalidPhotos.forEach((photo) => {
      if (photo.errors.type) {
        this.props.notifications.createError(
          <>
            {photo.file.name} - <FormattedMessage id="error.image.type" />
          </>
        );
      } else if (photo.errors.size) {
        this.props.notifications.createError(
          <>
            {photo.file.name} -{" "}
            <FormattedMessage id="error.image.maxSize" values={{ maxSize: prettyBytes(defaultMaxSize) }} />
          </>
        );
      }
    });

    if (validPhotos.length) {
      const { match } = this.props;
      const projectId = Number(match.params.projectId);
      const roomId = Number(match.params.roomId);

      await this.props.photos.createPhotos(projectId, roomId, validPhotos.map(({ file }) => file));
    }
  };

  private handleAddProductClose = () => {
    this.setState({ isAddProductOpen: false });
  };

  private checkIfSufficientCableSpacing = () => {
    const { roomDetails } = this.props.rooms;
    if (roomDetails && roomDetails.cableSpacing && roomDetails.cableSpacing < 0.05) {
      this.openCableSpacingWarningModal();
    }
  };

  private addProduct = () => {
    this.setState({ isAddProductOpen: true });
  };

  private openCableSpacingWarningModal = () => {
    this.setState({ isCableSpacingWarningModalOpen: true });
  };

  private closeCableSpacingWarningModal = () => {
    this.setState({ isCableSpacingWarningModalOpen: false });
  };

  private handleAddThermostatClose = () => {
    this.setState({ isAddThermostatOpen: false });
  };

  private handleAddAccessoryClose = () => {
    this.setState({ isAddAccessoryOpen: false });
  };

  private addThermostat = () => {
    this.setState({ isAddThermostatOpen: true });
  };

  private addAccessory = () => {
    this.setState({ isAddAccessoryOpen: true });
  };

  private closeRemoveAllProductsModal = () => {
    this.setState({ isRemoveAllProductsOpen: false });
  };

  private openRemoveAllProductsModal = () => {
    this.setState({ isRemoveAllProductsOpen: true });
  };

  private deleteAllProducts = async () => {
    const { match, products } = this.props;
    const projectId = Number(match.params.projectId);
    const roomId = Number(match.params.roomId);
    await products.removeAllProducts(projectId, roomId);
    this.closeRemoveAllProductsModal();
  };

  private productsList(products: Product[], projectFinished: boolean) {
    return products.map((item) => (
      <ProductItem
        key={item.id}
        product={item}
        onRemove={this.removeProductItem}
        onTest={this.onTestClick}
        disableActions={projectFinished}
      />
    ));
  }

  private removeProductItem = async (id: number) => {
    const { products } = this.props;
    const { projectId, roomId } = this.params;
    await products.removeProduct(projectId, roomId, id);
  };

  private thermostatsList(thermostats: BundledThermostat[], projectFinished: boolean) {
    return thermostats.map((item) => (
      <ThermostatItem
        key={item.id}
        thermostat={item}
        onRemove={this.removeThermostatItem}
        disableActions={!!item.source || projectFinished}
      />
    ));
  }

  private accessoriesList(accessories: BundledAccessory[], projectFinished: boolean) {
    return Object.values(groupBy(accessories, (item) => item.accessoryId)).map((items) => {
      const item = items[0];

      return (
        <AccessoryItem
          key={item.id}
          accessory={item}
          onRemove={this.removeAccessoryItem}
          disableActions={!!item.source || projectFinished}
          quantity={items.length}
        />
      );
    });
  }

  private removeThermostatItem = async (id: number) => {
    const { products } = this.props;
    const { projectId, roomId } = this.params;
    await products.removeThermostat(projectId, roomId, id);
  };

  private removeAccessoryItem = async (id: number) => {
    const { products } = this.props;
    const { projectId, roomId } = this.params;
    await products.removeAccessory(projectId, roomId, id);
  };

  private onTestClick = (productId: number) => {
    const {
      match: {
        params: { projectId, roomId },
      },
    } = this.props;
    this.props.history.push(`/projects/${projectId}/rooms/${roomId}/products/${productId}/tests`);
  };

  private renderToolTip(direction: string, label: string) {
    return (
      <HelpTip direction={direction}>
        <FormattedMessage id={`rooms.details.${label}.tooltip`} />
      </HelpTip>
    );
  }
}

export default compose<ProjectsProps, {}>(
  withRouter,
  inject("rooms", "projects", "photos", "products", "notifications"),
  observer
)(RoomDetails);
